import { ReactElement, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { regionStore } from '../../store/jotai/store';
import { strapiClient } from '../../core/api/strapi-client';
import { toastr } from '../../services/toast-service';
import { Logger } from '../../core/logger/Logger';
import './DeactivatedAccountBanner.scss';

/**
 * Site-wide prompt offering a deactivated user the way back.
 *
 * Deliberately rendered in the app chrome rather than on the login screen: a
 * deactivated user returning with a valid session cookie never passes through
 * the login form at all, so a login-only prompt would hide reactivation from
 * exactly the person it exists for.
 */
export const DeactivatedAccountBanner = (): ReactElement | null => {
  const { t } = useTranslation();
  const user = useAtomValue(userAtom, { store: regionStore });
  const [reactivating, setReactivating] = useState(false);

  if (!user?.deactivatedAt) {
    return null;
  }

  const handleReactivate = async () => {
    setReactivating(true);

    try {
      await strapiClient.reactivateAccount();
      // Re-read rather than patching the atom: the endpoint also restores the
      // user's content, so the profile is not the only thing that changed.
      await strapiClient.loadUser({ skipStripe: true });
      toastr.success(t('general.success'), t('deactivateAccount.reactivated'));
    } catch (error) {
      Logger.error(error);
      toastr.error(t('errors.error'), t('deactivateAccount.error'));
    } finally {
      setReactivating(false);
    }
  };

  return (
    <Alert
      severity='warning'
      className='deactivated-account-banner'
      action={
        <Button color='inherit' size='small' disabled={reactivating} onClick={handleReactivate}>
          {t('deactivateAccount.reactivate')}
        </Button>
      }>
      {t('deactivateAccount.bannerText')}
    </Alert>
  );
};
